/**
 * PhotoboothExportService.ts
 * Service pour exporter le canvas Konva en format JSON runtime photobooth
 */
import Konva from 'konva';
import { logger } from '../utils/ProfessionalLogger';
import {
  PhotoboothTemplate,
  PhotoboothElement,
  PhotoboothBackground,
  PhotoboothMetadata,
  validatePhotoboothTemplate,
} from '../types/PhotoboothTemplate';
import { PhotoTemplateService } from './PhotoTemplateService';
// TemplateValidatorService supprimé - utiliser ValidationService à la place
import { ValidationService } from './ValidationService';

export class PhotoboothExportService {
  private stage: Konva.Stage;
  private photoTemplateService?: PhotoTemplateService;
  private validator: ValidationService;

  constructor(stage: Konva.Stage, photoTemplateService?: PhotoTemplateService) {
    this.stage = stage;
    this.photoTemplateService = photoTemplateService;
    this.validator = ValidationService.getInstance();
  }

  /**
   * Récupère le nœud racine du template (le rectangle de fond)
   * Sert de référence pour les coordonnées (0,0) et les dimensions
   */
  private getTemplateReference(): {
    node: Konva.Node;
    x: number;
    y: number;
    width: number;
    height: number;
  } {
    // On cherche le rect qui a le nom 'template-bg' défini dans CanvasArea
    const bgNode = this.stage.findOne('.template-bg') as Konva.Rect | null;

    if (!bgNode) {
      // Fallback critique : si on ne trouve pas le fond, on essaie de deviner via les layers
      const contentLayer = this.stage.findOne('.content-layer') as Konva.Layer | null;
      if (contentLayer) {
        logger.warn(
          '⚠️ Template background node (.template-bg) not found. Export might be misaligned.'
        );
        // Essayer de trouver le backgroundLayer
        const backgroundLayer = this.stage.findOne('.backgroundLayer') as Konva.Group | null;
        if (backgroundLayer) {
          const layerPos = backgroundLayer.position();
          return {
            node: this.stage,
            x: layerPos.x,
            y: layerPos.y,
            width: this.stage.width() - layerPos.x * 2,
            height: this.stage.height() - layerPos.y * 2,
          };
        }
      }
      throw new Error("Structure du canvas invalide : Impossible de trouver 'template-bg'");
    }

    // Récupérer la position absolue du template dans le stage
    // Attention : bgNode est dans un Group qui a x=50, y=50.
    // getAbsolutePosition prend en compte toutes les transformations parentes.
    const absPos = bgNode.getAbsolutePosition();

    return {
      node: bgNode,
      x: absPos.x,
      y: absPos.y,
      width: bgNode.width(),
      height: bgNode.height(),
    };
  }

  /**
   * Exporte le canvas en format JSON runtime photobooth
   * CORRIGÉ: Asynchrone pour éviter le blocage UI avec toBlob
   */
  public async exportToPhotoboothFormat(): Promise<PhotoboothTemplate> {
    const ref = this.getTemplateReference();

    const canvasSize = {
      w: ref.width,
      h: ref.height,
    };

    // Extraire le fond (Async pour éviter de bloquer)
    const background = await this.extractBackground();

    // Extraire tous les éléments (avec correction des coordonnées)
    const elements = await this.extractElements(ref);

    // Compter les placeholders
    const placeholderCount = elements.filter(e => e.type === 'placeholder').length;
    const photoCount = Math.max(1, Math.min(4, placeholderCount || 1)) as 1 | 2 | 3 | 4;

    // Créer les métadonnées
    const metadata: PhotoboothMetadata = {
      version: '2.0.0', // Version bumpée
      createdAt: new Date().toISOString(),
      photoCount: String(photoCount) as '1' | '2' | '3' | '4',
      layout: this.photoTemplateService?.getCurrentLayout() || undefined,
    };

    const template: PhotoboothTemplate = {
      canvasSize,
      background,
      elements,
      metadata,
    };

    // Valider le template
    const validation = validatePhotoboothTemplate(template);
    if (!validation.success) {
      logger.error('❌ Erreur de validation du template:', validation.error);
      // On log mais on ne bloque pas forcément, selon la stratégie d'erreur
    }

    logger.info(
      `✅ Template photobooth exporté: ${photoCount} photo(s), ${elements.length} élément(s)`
    );
    return template;
  }

  /**
   * Extrait le fond du canvas (Correction Async)
   */
  private async extractBackground(): Promise<PhotoboothBackground | undefined> {
    const bgNode = this.stage.findOne('.template-bg') as Konva.Rect | null;

    // 1. Couleur de fond
    if (bgNode && bgNode.fill() && bgNode.fill() !== 'transparent') {
      return {
        type: 'color',
        value: bgNode.fill() as string,
      };
    }

    // 2. Image de fond (dans le groupe backgroundLayer)
    const bgLayerGroup = this.stage.findOne('.backgroundLayer') as Konva.Group | null;
    if (bgLayerGroup) {
      const bgImage = bgLayerGroup.findOne('Image') as Konva.Image | null;
      if (bgImage) {
        // Utiliser toDataURL de manière optimisée (synchrone en Konva mais rapide pour un seul node)
        const url = bgImage.toDataURL({ pixelRatio: 1 });
        return {
          type: 'image',
          value: url,
        };
      }
    }

    return { type: 'transparent' };
  }

  /**
   * Extrait tous les éléments en ignorant la structure React et en aplatissant
   * Mais en corrigeant les coordonnées relatives au template
   */
  private async extractElements(ref: { x: number; y: number }): Promise<PhotoboothElement[]> {
    const elements: PhotoboothElement[] = [];

    // On scanne les groupes nommés
    const groupsToScan = ['.photosLayer', '.overlayLayer'];

    for (const selector of groupsToScan) {
      const group = this.stage.findOne(selector) as Konva.Group | null;
      if (!group) continue;

      const children = group.getChildren();
      for (const node of children) {
        // Ignorer le template-bg (déjà traité dans extractBackground)
        if (node.name() === 'template-bg' || node.className === 'template-bg') {
          continue;
        }

        const element = await this.extractElementFromNode(node, ref);
        if (element) elements.push(element);
      }
    }

    // Trier par Z-index (approximatif via l'ordre dans le tableau + index explicite)
    elements.sort((a, b) => a.zIndex - b.zIndex);
    return elements;
  }

  /**
   * Extrait un élément depuis un nœud Konva
   * CORRIGÉ: Normalise les coordonnées en soustrayant l'offset du template
   */
  private async extractElementFromNode(
    node: Konva.Node,
    ref: { x: number; y: number }
  ): Promise<PhotoboothElement | null> {
    const type = this.determineElementType(node);
    if (!type) {
      return null;
    }

    // --- CORRECTION DES COORDONNÉES ---
    // On utilise la position absolue du nœud, moins la position absolue du template (offset)
    const absPos = node.getAbsolutePosition();
    const relativeX = absPos.x - ref.x;
    const relativeY = absPos.y - ref.y;

    // Prise en compte du scale du stage si nécessaire (si le stage est zoomé lors de l'export)
    const stageScale = this.stage.scaleX();
    const normalizedX = relativeX / stageScale;
    const normalizedY = relativeY / stageScale;

    const zIndex = node.getAttr('zIndex') || node.zIndex() || 0;
    const rotation = node.rotation() || 0;
    const opacity = node.opacity() ?? 1;

    interface BaseElement {
      type: string;
      id: string;
      x: number;
      y: number;
      width: number;
      height: number;
      [key: string]: unknown;
    }
    const baseElement: BaseElement = {
      type,
      id: node.id() || `el_${Math.random().toString(36).substr(2, 5)}`,
      x: normalizedX,
      y: normalizedY,
      // P3 : BaseElement attend width/height ; on les déduit du node.
      width: typeof node.width === 'function' ? node.width() : 0,
      height: typeof node.height === 'function' ? node.height() : 0,
      rotation,
      opacity,
      zIndex,
    };

    // Extraction spécifique (inchangée mais sécurisée)
    if (type === 'text' && node instanceof Konva.Text) {
      baseElement.content = node.text();
      baseElement.fontSize = node.fontSize();
      baseElement.fontFamily = node.fontFamily();
      baseElement.fill = node.fill();
      baseElement.w = node.width();
      baseElement.h = node.height();
    } else if (type === 'placeholder') {
      // Récupérer le photoIndex (peut être dans les attributs ou calculé)
      let photoIndex = node.getAttr('photoIndex');
      if (!photoIndex && node instanceof Konva.Group) {
        // Essayer de le déduire du Z-index (Z-index = 10 + index)
        const zIndex = node.getAttr('zIndex') || node.zIndex() || 10;
        if (zIndex >= 10 && zIndex <= 19) {
          photoIndex = zIndex - 9; // 10 -> 1, 11 -> 2, etc.
        }
      }
      if (photoIndex) {
        baseElement.photoIndex = photoIndex;
      }
      baseElement.w = node.width();
      baseElement.h = node.height();
    } else if (type === 'image' && node instanceof Konva.Image) {
      const image = node.image();
      if (image && image instanceof HTMLImageElement) {
        // Utiliser toDataURL pour les images
        const canvas = document.createElement('canvas');
        canvas.width = image.width;
        canvas.height = image.height;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(image, 0, 0);
          baseElement.src = canvas.toDataURL('image/png', 0.95);
        }
      }
      baseElement.w = node.width();
      baseElement.h = node.height();
    } else if (type === 'shape') {
      if (node instanceof Konva.Rect || node instanceof Konva.Circle) {
        baseElement.fill = node.fill() as string;
        baseElement.stroke = node.stroke() as string;
        baseElement.strokeWidth = node.strokeWidth();
        if (node instanceof Konva.Rect) {
          baseElement.w = node.width();
          baseElement.h = node.height();
        } else if (node instanceof Konva.Circle) {
          const radius = node.radius();
          baseElement.w = radius * 2;
          baseElement.h = radius * 2;
        }
      }
    }

    return baseElement as unknown as PhotoboothElement;
  }

  /**
   * Détermine le type d'élément depuis un nœud Konva
   * CORRIGÉ: Amélioration de la détection des photoSpace avec les nouveaux attributs
   */
  private determineElementType(node: Konva.Node): PhotoboothElement['type'] | null {
    // CORRIGÉ: Vérifier d'abord les attributs personnalisés (priorité haute)
    const objectType = node.getAttr('objectType');
    if (objectType === 'photoPlaceholder' || objectType === 'photoSpace') {
      return 'placeholder';
    }

    // Vérifier le nom du nœud
    const name = node.name();
    if (name === 'photoPlaceholder' || name === 'photoSpace') {
      return 'placeholder';
    }

    // CORRIGÉ: Vérifier si le nœud a un attribut photoIndex (indique un photoSpace)
    const photoIndex = node.getAttr('photoIndex');
    if (photoIndex !== undefined && photoIndex !== null) {
      return 'placeholder';
    }

    // Vérifier le type de classe Konva
    if (node instanceof Konva.Text) {
      return 'text';
    }
    if (node instanceof Konva.Image) {
      return 'image';
    }
    if (
      node instanceof Konva.Rect ||
      node instanceof Konva.Circle ||
      node instanceof Konva.Ellipse ||
      node instanceof Konva.Line ||
      node instanceof Konva.Arrow
    ) {
      return 'shape';
    }

    // Si c'est un groupe, vérifier son contenu et ses attributs
    if (node instanceof Konva.Group) {
      // CORRIGÉ: Vérifier d'abord les attributs du Group lui-même
      const groupObjectType = node.getAttr('objectType');
      const groupName = node.name();
      const groupPhotoIndex = node.getAttr('photoIndex');

      if (
        groupObjectType === 'photoPlaceholder' ||
        groupObjectType === 'photoSpace' ||
        groupName === 'photoPlaceholder' ||
        groupName === 'photoSpace' ||
        groupPhotoIndex !== undefined
      ) {
        return 'placeholder';
      }

      // Sinon, chercher dans les enfants
      const hasPlaceholder = node.findOne('.photoPlaceholder, .photoSpace, .placeholder');
      if (hasPlaceholder) {
        return 'placeholder';
      }
      const hasImage = node.findOne('.image, .photoImage');
      if (hasImage) {
        return 'image';
      }
      const hasText = node.findOne('.text');
      if (hasText) {
        return 'text';
      }
    }

    return null;
  }

  /**
   * Exporte le template en JSON string
   * CORRIGÉ: Asynchrone car exportToPhotoboothFormat est maintenant async
   */
  public async exportToJSONString(indent: number = 2): Promise<string> {
    const template = await this.exportToPhotoboothFormat();
    return JSON.stringify(template, null, indent);
  }

  /**
   * Exporte le template photobooth en fichiers séparés (Z-Depth Export)
   *
   * Génère 3 fichiers distincts :
   * 1. Overlay.png : Tout ce qui doit être PAR-DESSUS les photos (décorations, cadres, textes)
   * 2. Background.jpg : Tout ce qui doit être PAR-DERRIÈRE les photos (fond, images de fond)
   * 3. Coordinates.json : Les coordonnées des placeholders photos
   *
   * Cette séparation permet à la borne photobooth de glisser les photos entre le fond et l'overlay.
   */
  public async exportSeparatedFiles(
    options: {
      dpi?: number;
      quality?: number;
    } = {}
  ): Promise<{
    overlay: Blob; // PNG transparent
    background: Blob; // JPG avec fond
    coordinates: string; // JSON string
  }> {
    const { dpi = 300, quality = 0.95 } = options;
    const pixelRatio = dpi / 72; // Konva utilise 72 DPI par défaut pour le pixel ratio 1

    // 1. Trouver les groupes sources
    const backgroundGroup = this.stage.findOne('.backgroundLayer') as Konva.Group | undefined;
    const photosGroup = this.stage.findOne('.photosLayer') as Konva.Group | undefined;
    const overlayGroup = this.stage.findOne('.overlayLayer') as Konva.Group | undefined;

    // Référence dimensions (sans padding)
    const ref = this.getTemplateReference();

    // 2. Export BACKGROUND
    const backgroundBlob = await new Promise<Blob | null>((resolve, reject) => {
      if (!backgroundGroup) {
        resolve(null);
        return;
      }

      // Créer un stage temporaire pour l'export
      const tempStage = new Konva.Stage({
        width: ref.width * pixelRatio,
        height: ref.height * pixelRatio,
      });
      const tempLayer = new Konva.Layer();
      tempStage.add(tempLayer);

      // Cloner les éléments du backgroundGroup
      const layerOffset = backgroundGroup.position();
      backgroundGroup.find('*').forEach((node: Konva.Node) => {
        if (node instanceof Konva.Layer || node === backgroundGroup) return;
        if (node.name() === 'template-bg' || node.className === 'template-bg') {
          // Ajouter le template-bg comme fond
          const bgRect = new Konva.Rect({
            x: 0,
            y: 0,
            width: ref.width * pixelRatio,
            height: ref.height * pixelRatio,
            fill: (node as Konva.Rect).fill(),
          });
          tempLayer.add(bgRect);
          return;
        }

        const cloned = node.clone();
        const nodePos = node.position();
        cloned.x((nodePos.x - layerOffset.x) * pixelRatio);
        cloned.y((nodePos.y - layerOffset.y) * pixelRatio);
        cloned.scaleX(cloned.scaleX() * pixelRatio);
        cloned.scaleY(cloned.scaleY() * pixelRatio);
        tempLayer.add(cloned);
      });

      tempLayer.draw();

      // Exporter en JPG
      const dataURL = tempStage.toDataURL({
        mimeType: 'image/jpeg',
        quality,
        pixelRatio: 1,
      });

      fetch(dataURL)
        .then(response => response.blob())
        .then(resolve)
        .catch(reject);
    });

    if (!backgroundBlob) throw new Error('Erreur export background');

    // 3. Export OVERLAY (Transparent PNG)
    const overlayBlob = await new Promise<Blob | null>((resolve, reject) => {
      if (!overlayGroup) {
        resolve(null);
        return;
      }

      // Créer un stage temporaire pour l'export
      const tempStage = new Konva.Stage({
        width: ref.width * pixelRatio,
        height: ref.height * pixelRatio,
      });
      const tempLayer = new Konva.Layer();
      tempStage.add(tempLayer);

      // Cloner les éléments de l'overlayGroup
      const layerOffset = overlayGroup.position();
      overlayGroup.find('*').forEach((node: Konva.Node) => {
        if (node instanceof Konva.Layer || node === overlayGroup) return;

        const cloned = node.clone();
        const nodePos = node.position();
        cloned.x((nodePos.x - layerOffset.x) * pixelRatio);
        cloned.y((nodePos.y - layerOffset.y) * pixelRatio);
        cloned.scaleX(cloned.scaleX() * pixelRatio);
        cloned.scaleY(cloned.scaleY() * pixelRatio);
        tempLayer.add(cloned);
      });

      tempLayer.draw();

      // Exporter en PNG (avec transparence)
      const dataURL = tempStage.toDataURL({
        mimeType: 'image/png',
        pixelRatio: 1,
      });

      fetch(dataURL)
        .then(response => response.blob())
        .then(resolve)
        .catch(reject);
    });

    if (!overlayBlob) throw new Error('Erreur export overlay');

    // 4. Export COORDINATES
    // On itère sur les enfants de photosGroup
    interface Coordinate {
      x: number;
      y: number;
      width: number;
      height: number;
      index: number;
      [key: string]: unknown;
    }
    const coordinates: Coordinate[] = [];
    if (photosGroup) {
      const layerOffset = photosGroup.position();
      photosGroup.getChildren().forEach(node => {
        // Calculer coord relative
        const nodePos = node.position();
        const relX = nodePos.x - layerOffset.x;
        const relY = nodePos.y - layerOffset.y;

        const photoIndex = node.getAttr('photoIndex') || 1;

        // P3 : Coordinate type strict ; on cast pour préserver les attrs custom.

        coordinates.push({
          id: node.id(),
          x: relX,
          y: relY,
          w: node.width(),
          h: node.height(),
          rotation: node.rotation(),
          index: photoIndex,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any);
      });
    }

    return {
      background: backgroundBlob,
      overlay: overlayBlob,
      coordinates: JSON.stringify(coordinates, null, 2),
    };
  }
}
